import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSuperAdminInternalValidation, SAInternalValidation, SAIVSignal } from '../../services/api';
import SuperAdminLayout from './SuperAdminLayout';

/** Internal Validation lens — the R&D "durable skill" signals, central-only.
 *  Per the design note, this layer is never shown to schools; it's the real
 *  test of whether productive struggle is producing transfer. */
const SuperAdminInternalValidation: React.FC = () => {
  const navigate = useNavigate();
  const [d, setD] = useState<SAInternalValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login', { replace: true });
      return;
    }
    getSuperAdminInternalValidation(token)
      .then(setD)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('superAdminToken');
          navigate('/super-admin/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const live = (d?.signals || []).filter((s) => s.state === 'live');
  const pending = (d?.signals || []).filter((s) => s.state === 'pending');

  const LiveCard: React.FC<{ s: SAIVSignal }> = ({ s }) => {
    const noData = s.value == null;
    const total = s.dist.reduce((a, b) => a + b.count, 0) || 1;
    return (
      <div className={`tile${noData ? ' dark-ship' : ''}`}>
        <div className="lab">{s.label}</div>
        <div className="val">{noData ? '—' : s.value}</div>
        {s.detail ? <div className="tgt">{s.detail}</div> : null}
        {noData ? (
          <span className="capture">no evaluations yet — populates as chats are analysed</span>
        ) : null}
        {s.dist.length ? (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {s.dist.map((x) => (
              <div key={x.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                <span style={{ width: 92, color: 'var(--faint)', textTransform: 'capitalize' }}>{x.label.replace(/_/g, ' ')}</span>
                <span className="meter" style={{ flex: 1, marginTop: 0 }}>
                  <i className="fill-acc" style={{ width: `${(x.count / total) * 100}%` }} />
                </span>
                <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{x.count}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <SuperAdminLayout>
      <div className="content">
        <h1 className="pg">Internal validation</h1>
        <p className="pg-sub">
          The durable-skill signals — the real test of whether productive struggle is producing transfer.
        </p>
        <div className="note w" style={{ marginTop: 0, marginBottom: 16 }}>
          <b>Internal · R&amp;D only.</b> Never shown to schools or facilitators — this drives product iteration, not procurement conversations.
        </div>

        {loading ? (
          <p className="hint">Loading…</p>
        ) : error || !d ? (
          <p className="hint" style={{ color: 'var(--crit)' }}>{error || 'Not found.'}</p>
        ) : (
          <>
            <div className="modtag"><span className="rd rd-ready" /><span className="nm">Live signals</span> · from {d.conversations_evaluated} analysed conversations</div>
            <div className="grid g3">
              {live.map((s) => <LiveCard key={s.key} s={s} />)}
            </div>

            <div className="modtag mt14"><span className="rd rd-cap" /><span className="nm">Pending instrumentation</span> · the design note's core metrics, not captured yet</div>
            <div className="grid g3">
              {pending.map((s) => (
                <div key={s.key} className="tile dark-ship">
                  <div className="lab">{s.label}</div>
                  <div className="val">—</div>
                  {s.detail ? <div className="tgt">{s.detail}</div> : null}
                  <span className="capture">not instrumented yet</span>
                </div>
              ))}
            </div>

            <div className="note mt14">
              <b>Why this matters.</b> The school-facing dashboards prove coverage and scores; this layer is what proves the <i>durable-skill</i> thesis — and eventually becomes the differentiation story against pure content-delivery tutors.
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminInternalValidation;
